import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Box, ListItem, ListItemIcon, ListItemText, MenuItem, MenuList, Stack, Theme, Typography } from "@mui/material";

import { signOut } from "aws-amplify/auth";
import { useDispatch, useSelector } from "react-redux";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Navigation, NavigationItem, NavigationPageItem } from "@toolpad/core/AppProvider";

import { Loader } from "@components/Loader";
import { RootState, store } from "@store/store";
import { PopupManager } from "@components/PopupManager";
import { DialogManager } from "@components/DialogManager";
import { setExpandStateLayoutSidebar } from "@store/slices/componentsSlice";
import {
    DashboardHeaderProps,
    DashboardLayout,
    DashboardSidebarPageItem,
    SidebarFooterProps,
} from "@toolpad/core/DashboardLayout";

// import { PyMioTheme } from "@themes/PyMioTheme.ts";
// import { FinbraTheme } from "@themes/FinbraTheme.ts";
// import { KapitalFlexTheme } from "@themes/KapitalFlexTheme.ts";
import { BackOfficeTheme } from "@themes/BackOfficeTheme.ts";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { CustomHeader } from "@components/CustomHeader.tsx";
import { useTokenExpirationGuard } from "@hooks/useTokenExpirationGuard";

export const AdminLayout = () => {
    // const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [themeToLoad, setThemeToLoad] = useState<Theme>(BackOfficeTheme);

    // const sessionInfo = (state = store.getState()) => state.session;
    const expandLayoutSidebarState = (state = store.getState()) => state.components.layout.sidebar;

    const userSession = useSelector((state: RootState) => state.session.user);
    const permissions = useSelector((state: RootState) => state.session.permissions);
    const applicationLoaded = useSelector((state: RootState) => state.components.applicationSelected);
    const lockMenuApplication = useSelector((state: RootState) => state.components.menuApplication.lock);

    useTokenExpirationGuard();

    const navigationOptions: Navigation = useMemo(() => {
        let options: NavigationItem[] = [];

        if (permissions.find((t) => t.module.code === "SBUSERS")) {
            options.push({
                segment: "admin/usuarios",
                title: "Usuarios",
            });
        }
        if (permissions.find((t) => t.module.code === "SBROL")) {
            options.push({
                segment: "admin/roles",
                title: "Roles",
            });
            options.push({
                kind: "divider",
            });
        }
        if (permissions.find((t) => t.module.code === "SBCMPN")) {
            options.push({
                segment: "admin/companias",
                title: "Compañías",
            });
        }
        if (permissions.find((t) => t.module.code === "SBFINAN")) {
            options.push({
                segment: "admin/financieras",
                title: "Financieras",
            });
            options.push({
                kind: "divider",
            });
        }
        if (permissions.find((t) => t.module.code === "SBCOAT")) {
            options.push({
                segment: "admin/codigos_atribucion",
                title: "Códigos de Atribución",
            });
        }
        if (permissions.find((t) => t.module.code === "SBESAT")) {
            options.push({
                segment: "admin/estructura_atribucion",
                title: "Estructura Atribución",
            });
            options.push({
                kind: "divider",
            });
        }
        if (permissions.find((t) => t.module.code === "SBARREV")) {
            options.push({
                segment: "admin/abortivos_revision",
                title: "Reglas de Admisión",
            });
            options.push({
                kind: "divider",
            });
        }
        if (permissions.find((t) => t.module.code === "SBRARFI")) {
            options.push({
                segment: "admin/abortivos_revision_financieras",
                title: "Reglas Adm. Finan.",
            });
            options.push({
                kind: "divider",
            });
        }
        if (permissions.find((t) => t.module.code === "SBLOAC")) {
            options.push({
                segment: "admin/log_actividad",
                title: "Log Actividad",
            });
            options.push({
                kind: "divider",
            });
        }
        if (permissions.find((t) => t.module.code === "SBREGRAL")) {
            options.push({
                segment: "admin/general",
                title: "General",
            });
        }
        if (permissions.find((t) => t.module.code === "SBRESOL")) {
            options.push({
                segment: "admin/solicitudes",
                title: "Solicitudes",
            });
            options.push({
                kind: "divider",
            });
        }

        return options;
    }, [location.pathname]);

    const customHeader = (props: DashboardHeaderProps) => {
        return <CustomHeader {...props} />;
    };

    const sidebarFooter = (props: SidebarFooterProps) => {
        return (
            <Stack direction={"column"}>
                <MenuList>
                    <MenuItem
                        onClick={() =>
                            dispatch(
                                setExpandStateLayoutSidebar(!expandLayoutSidebarState(store.getState()).expandState),
                            )
                        }
                    >
                        <ListItemText>{props.mini ? "" : "Ocultar menú"}</ListItemText>
                        <ListItemIcon>{props.mini ? <MenuIcon /> : <MenuOpenIcon />}</ListItemIcon>
                    </MenuItem>
                </MenuList>
            </Stack>
        );
    };

    const handleLogout = async () => {
        dispatch({ type: "CLEAN_STORE" });
        await signOut();
        navigate("/");
    };

    const handleRenderPageItem = useCallback(
        (item: NavigationPageItem, { mini }: { mini: boolean }) => {
            if (item.segment?.startsWith("header")) {
                return (
                    <ListItem sx={{ justifyContent: "center", p: 0 }}>
                        <Typography component={"span"} variant="h5" fontWeight={300} color="main2">
                            {mini === false && `ID Solicitud: `}
                            <Typography component={"span"} variant="h5" fontWeight={600} color="main2">
                                {applicationLoaded.id_application_company?.toString().padStart(5, "0")}
                            </Typography>
                        </Typography>
                    </ListItem>
                );
            }

            return <DashboardSidebarPageItem item={item} disabled={false} />;
        },
        [applicationLoaded.id, location.pathname, lockMenuApplication],
    );

    return (
        <ReactRouterAppProvider
            theme={themeToLoad}
            navigation={navigationOptions}
            session={{
                user: {
                    name: userSession.name?.concat(
                        " ",
                        userSession.last_name ?? "",
                        " ",
                        userSession.last_name_2 ?? "",
                    ),
                },
            }}
            authentication={{
                signOut: handleLogout,
                signIn: () => {},
            }}
        >
            <DashboardLayout
                hideNavigation={false}
                renderPageItem={handleRenderPageItem}
                sidebarExpandedWidth={220}
                slots={{
                    sidebarFooter: sidebarFooter,
                    header: customHeader,
                }}
                sx={{ backgroundColor: "#f1f2f7" }}
            >
                <Suspense fallback={<Loader />}>
                    <Box
                        py={3}
                        px={4}
                        sx={{ height: { xs: "auto", lg: "100vh" }, minHeight: { xs: "100vh", lg: "unset" } }}
                        id="content"
                    >
                        <Outlet />
                    </Box>
                </Suspense>
            </DashboardLayout>
            <DialogManager />
            <PopupManager />
        </ReactRouterAppProvider>
    );
};
